import moment, { Moment } from 'moment';
import { isNumber, isNil } from 'lodash-es';
import { DATE_FORMAT, DATE_TIME_FORMAT, TIME_FORMAT } from 'app-constants';
import { Nullable } from 'app-types';
import { getTextValue } from 'utils/utils';

export function secondsToMinutes(
	seconds: number
): {
	value: string;
	preferSuffix: string;
} {
	let format = 'mm';
	let preferSuffix = 'min';
	const hourInSeconds = 3600;
	if (seconds >= hourInSeconds) {
		format = TIME_FORMAT;
		preferSuffix = 'hour';
	}
	const value = moment()
		.startOf('day')
		.seconds(seconds)
		.format(format);
	return {
		value,
		preferSuffix,
	};
}

export function metersToMiles(meters: number, suffix?: string): string {
	const miles = meters * 0.000621371192;
	return `${miles.toFixed(1)} ${suffix ? suffix : ''}`;
}

export function metersToKilometers(meters: number, suffix?: string): string {
	const miles = meters / 1000;
	return `${miles.toFixed(1)} ${suffix ? suffix : ''}`;
}

export function metersTo(meters: number, to: 'km' | 'mi', suffix: typeof to = to) {
	if (to === 'mi') {
		return metersToMiles(meters, suffix);
	}
	return metersToKilometers(meters, suffix);
}

export function roundNumber(value: Nullable<number | string>, precisions: number = 2): string {
	if (isNil(value)) {
		return '';
	}
	if (typeof value === 'string') {
		const v = Number(value);
		if (isNumber(v)) {
			value = v;
		} else {
			return value;
		}
	}

	const precisionRate = Math.pow(10, precisions);

	return (Math.round(value * precisionRate) / precisionRate).toFixed(precisions);
}

export function priceWithCurrency(value: number, currencySymbol: string) {
	return `${currencySymbol} ${roundNumber(value)}`;
}

export function getTimeZone(date?: string | Moment) {
	let offset;
	if (date) {
		const { _tzm: tzm } = moment(date) as any;
		if (tzm !== undefined) {
			offset = tzm / 60;
		} else {
			return '';
		}
	} else {
		return '';
	}
	return offset > 0 ? '+' + offset : offset;
}

export function dateTimeZone(date?: string | Moment, format: string = DATE_TIME_FORMAT) {
	if (!date) {
		return '';
	}
	return moment.parseZone(date).format(format);
}

export function datetimeWithTimeZone(date: string | Moment) {
	if (!date) {
		return '';
	}
	return `${dateTimeZone(date)} (UTC ${getTimeZone(date)})`;
}

export function toDate(date: Nullable<string | Moment>, format: string = DATE_FORMAT) {
	if (date) {
		return moment(date).format(format);
	}
	return getTextValue(date);
}

export function toTime(time: moment.Moment | string) {
	if (typeof time !== 'string') {
		return moment(time).format(TIME_FORMAT);
	}
	return time;
}

export function formatTime(time?: string) {
	if (time) {
		return moment(time, 'HH:mm:ss').format(TIME_FORMAT);
	}
	return time;
}

export function toNegative(value: Nullable<number>) {
	return isNil(value) ? value : value * -1;
}
export function toAbs(value: Nullable<number>) {
	return isNil(value) ? value : Math.abs(value);
}
